import { useEffect } from 'react';
import { appVersion, isRequestValidationError, isStaleBundleRejection, STALE_BUNDLE_SAVE_MESSAGE, useIsBundleStale } from '../lib/app-version';
import { inlineErrorMessage } from '../lib/error-handling';
import { Alert } from './ui/Alert';
import { TextButton } from './ui/TextLink';

interface Props {
  error: unknown;
  fallback: string;
  /** Turns the error into the text shown; defaults to `inlineErrorMessage`. */
  messageFor?: (error: unknown, fallback: string) => string;
}

/**
 * Inline error for a failed save. When the backend rejected the request as
 * malformed and a newer bundle is deployed, the cause is the tab, not the input,
 * so it asks the user to reload instead of showing the validation message.
 */
export function SaveErrorAlert({ error, fallback, messageFor = inlineErrorMessage }: Props) {
  const bundleStale = useIsBundleStale();

  useEffect(() => {
    if (isRequestValidationError(error)) void appVersion.check({ force: true });
  }, [error]);

  if (isStaleBundleRejection(error, bundleStale)) {
    return (
      <Alert variant="error" role="alert">
        {STALE_BUNDLE_SAVE_MESSAGE}{' '}
        <TextButton tone="notice" onClick={() => window.location.reload()}>
          Reload to try again
        </TextButton>
      </Alert>
    );
  }

  return <Alert variant="error" role="alert">{messageFor(error, fallback)}</Alert>;
}
